import { Button, Space, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "shared/routes";
import { refreshMethods, sendCode } from "../lib/auth-actions";
import { AuthError } from "../lib/auth-error";
import {
  AuthSession,
  FAILED_STATUSES,
  formatPhone,
  loadAuthSession,
} from "../lib/auth-session";
import { useAuthRestart } from "../lib/use-auth-restart";
import { useCountdown } from "../lib/use-countdown";
import { AuthChannel } from "../types";

/** Выбор канала доставки кода из списка, который вернул сервер. */
export const AuthMethodsForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [session, setSession] = useState<AuthSession | null>(loadAuthSession);
  // Каналы только что получены на вводе телефона — повторный /methods не нужен.
  const isFresh = !!(location.state as { fresh?: boolean } | null)?.fresh;
  const [isRefreshing, setIsRefreshing] = useState(!isFresh);
  const [sendingChannel, setSendingChannel] = useState<AuthChannel>();
  const [error, setError] = useState("");
  const cooldown = useCountdown(session?.nextSendAt);
  const { restart, handleAuthError, reloadSession, search } = useAuthRestart({
    expiresAt: session?.expiresAt,
    watchExpiry: true,
  });

  const handleError = (authError: AuthError, current: AuthSession | null) => {
    const result = handleAuthError(authError, current);
    if (result !== true) {
      setError(result);
    }
  };

  useEffect(() => {
    const current = loadAuthSession();
    if (!current) {
      restart();
      return;
    }
    if (isFresh) {
      // Чтобы возврат «назад» на этот экран снова обновлял каналы.
      navigate(`${ROUTES.AUTH_METHODS}?${search}`, { replace: true });
      return;
    }

    // Обновляем каналы: после отправки Telegram он возвращается только так.
    let cancelled = false;
    refreshMethods(current)
      .then((next) => !cancelled && setSession(next))
      .catch((authError: AuthError) => {
        if (!cancelled) handleError(authError, current);
      })
      .finally(() => !cancelled && setIsRefreshing(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = async (channel: AuthChannel) => {
    const current = loadAuthSession();
    if (!current) {
      restart();
      return;
    }

    setError("");
    setSendingChannel(channel);
    try {
      const next = await sendCode(current, channel);
      setSession(next);

      const last = next.deliveries[next.deliveries.length - 1];
      if (last && FAILED_STATUSES.includes(last.delivery_status)) {
        setError(
          t("code.delivery_failed", { channel: t(`auth.channels.${channel}`) }),
        );
        return;
      }

      navigate(`${ROUTES.CODE}?${search}`);
    } catch (e) {
      const authError = e as AuthError;
      const latest = reloadSession();
      if (!latest) {
        return;
      }
      setSession(latest);

      if (authError.code === "CHANNEL_UNAVAILABLE") {
        refreshMethods(latest)
          .then(setSession)
          .catch(() => {});
      }
      handleError(authError, latest);
    } finally {
      setSendingChannel(undefined);
    }
  };

  if (!session) {
    return null;
  }

  const channels = session.channels;

  return (
    <div>
      <Typography.Title level={3}>{t("auth_methods.title")}</Typography.Title>
      <p>{t("auth_methods.text", { phone: formatPhone(session.phone) })}</p>

      {isRefreshing && !channels.length ? (
        <Spin style={{ display: "block", margin: "24px auto" }} />
      ) : (
        <Space vertical size="middle" style={{ display: "flex" }}>
          {!channels.length && (
            <Typography.Text type="secondary">
              {t("auth_methods.empty")}
            </Typography.Text>
          )}

          {channels.map((channel) => (
            <Button
              key={channel}
              block
              type="primary"
              size="large"
              loading={sendingChannel === channel}
              disabled={
                cooldown > 0 || (!!sendingChannel && sendingChannel !== channel)
              }
              onClick={() => select(channel)}
            >
              {t(`auth.channels.${channel}`)}
            </Button>
          ))}

          {cooldown > 0 && !!channels.length && (
            <Typography.Text type="secondary">
              {t("auth_methods.cooldown", { seconds: cooldown })}
            </Typography.Text>
          )}
        </Space>
      )}

      {!!error && (
        <Typography.Text
          style={{ marginTop: 16, display: "block" }}
          type="danger"
        >
          {error}
        </Typography.Text>
      )}

      <Button
        block
        type="text"
        style={{ marginTop: 16 }}
        onClick={() => restart()}
      >
        {t("code.change_number")}
      </Button>
    </div>
  );
};
